// SPDX-License-Identifier: Apache-2.0 AND LicenseRef-Pachamama-Proprietary
// Copyright 2026 The Zyra Project
// Modifications copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * Telemetry transport — the POST side of the emitter.
 *
 * Separated from emitter.ts so the queueing logic stays focused on
 * state and the network layer is trivially mockable from tests. The
 * emitter imports the `Transport` interface and either gets a real
 * one from `createFetchTransport()` at app init or a mock from a
 * test setup.
 *
 * Two responsibilities:
 *
 *  1. Send a batch to `/api/ingest` via `fetch()`.
 *  2. Unload-time flush via `navigator.sendBeacon()` — the one API
 *     that reliably delivers a POST as the page is closing.
 *
 * AYNI: TerraViz's desktop (Tauri) halves — the Tauri HTTP plugin, an
 * absolute ingest URL for the desktop webview, and the offline queue
 * persisted under `sos-telemetry-queue` — went with the desktop app
 * (2026-10-06).
 *
 * Response semantics (mirrors functions/api/ingest.ts):
 *   204  accepted — drop locally, reset backoff
 *   410  kill switch on — permanent for the session, stop trying
 *   429  rate limited — retryable with backoff
 *   5xx  server error — retryable with backoff
 *   4xx  other — drop the batch (malformed / unauthorized origin)
 *   network error → retryable (offline, DNS, TLS, etc.)
 */

import type { TelemetryEvent } from '../types'

// ---------------------------------------------------------------
// Config
// ---------------------------------------------------------------

/**
 * Ingest path, relative to the page's origin: the Pages Function that
 * serves it is part of the same deploy that served the page.
 */
export const DEFAULT_ENDPOINT = '/api/ingest'

// ---------------------------------------------------------------
// Types
// ---------------------------------------------------------------

export interface SendResult {
  /** HTTP status code, or null on network / transport failure. */
  status: number | null
  /** Accepted by the server — safe to drop the batch locally. */
  ok: boolean
  /** Caller should re-queue the events for a later attempt. */
  retryable: boolean
  /** Session-long cooldown requested (410). Caller should stop
   * attempting sends until the app is relaunched. */
  permanent: boolean
}

export interface Transport {
  /** Async POST. Returns a classification the emitter maps into
   * queue / cooldown / backoff actions. */
  send(sessionId: string, events: TelemetryEvent[]): Promise<SendResult>
  /** Unload-time POST via `navigator.sendBeacon()`. Returns whether
   * the browser accepted the beacon — `false` means the emitter
   * keeps the events queued. */
  sendBeacon(sessionId: string, events: TelemetryEvent[]): boolean
  /** Endpoint URL used by this transport — exposed for logging
   * and tests. */
  readonly endpoint: string
}

export interface TransportOptions {
  endpoint?: string
  /** Override the underlying fetch implementation. Used in tests. */
  fetchImpl?: typeof globalThis.fetch
  /** Override `navigator.sendBeacon`. Used in tests. */
  beaconImpl?: (url: string, body: BodyInit) => boolean
}

// ---------------------------------------------------------------
// Transport factory
// ---------------------------------------------------------------

/**
 * Build a real network transport. Safe to call at app init.
 */
export function createFetchTransport(options: TransportOptions = {}): Transport {
  const endpoint = options.endpoint ?? DEFAULT_ENDPOINT

  async function send(
    sessionId: string,
    events: TelemetryEvent[],
  ): Promise<SendResult> {
    if (events.length === 0) {
      return { status: 204, ok: true, retryable: false, permanent: false }
    }
    const body = JSON.stringify({ session_id: sessionId, events })
    let resp: Response
    try {
      const fetcher = options.fetchImpl ?? globalThis.fetch
      resp = await fetcher(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        // `same-origin`, not `omit`: a node whose hostname sits
        // behind Cloudflare Access answers an uncredentialed
        // `/api/ingest` POST with a 302 to the login origin, which
        // sends no `Access-Control-Allow-Origin` — so the send fails
        // as a CORS error and retries forever. This does not widen
        // what the server sees: `functions/api/ingest.ts` reads
        // `Origin`, `Content-Length`, `CF-Connecting-IP` and
        // `CF-IPCountry` and never looks at a cookie. Nor does it
        // leak cross-origin — `same-origin` withholds cookies from
        // an absolute `endpoint` override exactly as `omit` did.
        // `sendBeacon()` below has always
        // sent them; this makes the two paths agree.
        credentials: 'same-origin',
        keepalive: true,
      })
    } catch {
      // Network-level failure (offline, DNS, TLS, CORS, etc.) — retryable.
      return { status: null, ok: false, retryable: true, permanent: false }
    }
    return classifyResponse(resp.status)
  }

  function sendBeacon(
    sessionId: string,
    events: TelemetryEvent[],
  ): boolean {
    if (events.length === 0) return true
    const beacon = options.beaconImpl ?? navigatorBeacon()
    if (!beacon) return false
    try {
      const body = new Blob(
        [JSON.stringify({ session_id: sessionId, events })],
        { type: 'application/json' },
      )
      return beacon(endpoint, body)
    } catch {
      return false
    }
  }

  return { send, sendBeacon, endpoint }
}

/** Map an HTTP status code to a SendResult. Exposed for tests. */
export function classifyResponse(status: number): SendResult {
  if (status === 204) {
    return { status, ok: true, retryable: false, permanent: false }
  }
  if (status === 410) {
    return { status, ok: false, retryable: false, permanent: true }
  }
  if (status === 429 || (status >= 500 && status < 600)) {
    return { status, ok: false, retryable: true, permanent: false }
  }
  // Any other 4xx (400 invalid body, 403 origin, 413 too large) —
  // dropping the batch is safer than retrying a malformed payload.
  return { status, ok: false, retryable: false, permanent: false }
}

function navigatorBeacon():
  | ((url: string, body: BodyInit) => boolean)
  | null {
  if (typeof navigator === 'undefined') return null
  if (typeof navigator.sendBeacon !== 'function') return null
  return navigator.sendBeacon.bind(navigator)
}

